import type { GameStateManager } from '../game/GameState';
import type { GameData, LocationId, Anomaly } from '../game/types';
import { AudioManager } from '../audio/AudioManager';
import { getAnomalyDisplayName } from '../utils/AnomalyNameResolver';

export class CellDisplay {
  constructor(
    private container: HTMLElement,
    private locationId: LocationId,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onSelect: (id: LocationId) => void,
    private onSelectResearcher?: (id: string) => void,
    private onMoveOrder?: (locationId: LocationId) => void
  ) {
    // Use event delegation - attach once
    if (!this.container.dataset.listenersAttached) {
      // Left click - select location or researcher
      this.container.addEventListener('click', (e) => {
        // Check if click is directly on researcher avatar or its child elements
        const target = e.target as HTMLElement;
        const avatar = target.closest('.researcher-avatar, .researcher-avatar-arriving');
        
        if (avatar && this.onSelectResearcher) {
          const researcherId = avatar.getAttribute('data-researcher-id');
          if (researcherId) {
            e.stopPropagation();
            e.preventDefault();
            AudioManager.getInstance().playClick();
            // Direct selection without setTimeout to avoid rapid flashing
            this.onSelectResearcher(researcherId);
            return;
          }
        }
        // Otherwise select the location
        e.stopPropagation();
        AudioManager.getInstance().playClick();
        this.onSelect(this.locationId);
      });

      // Right click - issue move order if researcher is selected
      this.container.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Don't issue move order if clicking on researcher avatar
        const avatar = (e.target as HTMLElement).closest('.researcher-avatar');
        if (avatar) {
          return;
        }

        // Issue move order if callback is provided
        if (this.onMoveOrder) {
          this.onMoveOrder(this.locationId);
        }
      });

      this.container.dataset.listenersAttached = 'true';
    }
    this.update();
  }

  update(): void {
    const state = this.gameState.getState();
    const selected = state.selectedEntity;
    const isSelected = selected.type === 'location' && selected.id === this.locationId;
    
    // Check if cell_4 has disappeared
    if (this.locationId === 'cell_4' && this.gameState.isCell4Disappeared()) {
      this.renderDisappearedCell();
      return;
    }
    
    const anomaly = this.gameState.getAnomalyInCell(this.locationId);
    const breachedFromHere = this.gameState.getBreachedFromCell(this.locationId);
    const breachedInHere = this.gameState.getBreachedInCell(this.locationId);
    const strings = this.gameData.uiStrings;
    const locationName = strings.locations?.[this.locationId] || this.locationId;

    // Find all researchers that should be displayed in this room
    // Show researchers that are at this location and not currently in transit
    const researchersHere = state.researchers.filter(r => {
      // Show if they're in this room and not moving (targetLocation is null)
      if (r.location === this.locationId && r.targetLocation === null) return true;
      // Don't show researchers that are moving (they're handled by UIManager transit system)
      return false;
    });
    const activeResearcher = researchersHere.find(r => r.currentAction === 'Research');

    // Update class without recreating - breached class only for ORIGIN cells
    const isBreached = breachedFromHere !== undefined;
    this.container.className = `cell-display ${isSelected ? 'selected' : ''} ${isBreached ? 'breached' : ''} ${this.locationId}`;

    // Only update content, not structure
    let progressBar = '';
    if (activeResearcher && activeResearcher.actionTotalTime > 0) {
      const progress = (1 - activeResearcher.actionTimer / activeResearcher.actionTotalTime) * 100;
      progressBar = `
        <div class="cell-progress-bar">
          <div class="progress-fill" style="width: ${progress}%"></div>
        </div>
      `;
    }

    let anomalyDisplay = '';
    const propertyColors = strings.propertyColors || {};
    if (breachedFromHere) {
      // Show breached anomaly info at ORIGIN cell (where it escaped FROM)
      const breachedAnomaly = breachedFromHere.originalAnomaly;
      const displayName = getAnomalyDisplayName(breachedAnomaly, this.gameData);
      const knownProps = breachedAnomaly.knownProperties.map(p => {
        const symbol = strings.propertySymbols?.[p.toLowerCase()] || p;
        const propColor = propertyColors[p.toLowerCase()] || '#6bb0ff';
        return `<span class="property known" data-tooltip-key="property.${p.toLowerCase()}" style="border-color: ${propColor}; color: ${propColor};">${symbol}</span>`;
      }).join('');
      const unknownCount = 3 - breachedAnomaly.knownProperties.length;
      const unknownProps = Array(unknownCount).fill(0).map(() => 
        `<span class="property unknown">${strings.properties?.unknown || '?'}</span>`
      ).join('');
      
      const escapeTime = breachedFromHere.escapeTimer;
      const isContaining = breachedFromHere.isContaining;
      
      anomalyDisplay = `
        <div class="anomaly-info breached-anomaly">
          <div class="breach-warning">⚠️ BREACHED</div>
          <div class="anomaly-name">${displayName}</div>
          <div class="anomaly-properties">
            ${knownProps}
            ${unknownProps}
          </div>
          ${isContaining ? '<div class="containment-status">Containing...</div>' : `<div class="escape-timer">Escape in: ${escapeTime} ticks</div>`}
        </div>
      `;
    } else if (anomaly) {
      const displayName = getAnomalyDisplayName(anomaly, this.gameData);
      const knownProps = anomaly.knownProperties.map(p => {
        const symbol = strings.propertySymbols?.[p.toLowerCase()] || p;
        const propColor = propertyColors[p.toLowerCase()] || '#6bb0ff';
        return `<span class="property known" data-tooltip-key="property.${p.toLowerCase()}" style="border-color: ${propColor}; color: ${propColor};">${symbol}</span>`;
      }).join('');
      const unknownCount = 3 - anomaly.knownProperties.length;
      const unknownProps = Array(unknownCount).fill(0).map(() => 
        `<span class="property unknown">${strings.properties?.unknown || '?'}</span>`
      ).join('');
      
      anomalyDisplay = `
        <div class="anomaly-info">
          <div class="anomaly-name">${displayName}</div>
          <div class="anomaly-properties">
            ${knownProps}
            ${unknownProps}
          </div>
        </div>
      `;
    } else if (this.locationId !== 'control_room') {
      anomalyDisplay = `<div class="cell-empty">${strings.labels?.cell_empty || 'Empty Cell'}</div>`;
    } else {
      anomalyDisplay = `<div class="cell-control-room">${strings.labels?.control_room || 'Control Room'}</div>`;
    }

    // Update progress bar separately
    let existingProgress = this.container.querySelector('.cell-progress-bar');
    if (progressBar && !existingProgress) {
      this.container.insertAdjacentHTML('afterbegin', progressBar);
    } else if (!progressBar && existingProgress) {
      existingProgress.remove();
    } else if (existingProgress && activeResearcher) {
      const fill = existingProgress.querySelector('.progress-fill') as HTMLElement;
      if (fill && activeResearcher.actionTotalTime > 0) {
        const progress = (1 - activeResearcher.actionTimer / activeResearcher.actionTotalTime) * 100;
        fill.style.width = `${progress}%`;
      }
    }

    // Get or create content structure - preserve DOM elements
    let contentDiv = this.container.querySelector('.cell-content') as HTMLElement;
    if (!contentDiv) {
      // Initial setup - create full structure
      contentDiv = document.createElement('div');
      contentDiv.className = 'cell-content';
      this.container.appendChild(contentDiv);
    }

    // Ensure header exists
    let headerDiv = contentDiv.querySelector('.cell-header') as HTMLElement;
    if (!headerDiv) {
      headerDiv = document.createElement('div');
      headerDiv.className = 'cell-header';
      contentDiv.insertBefore(headerDiv, contentDiv.firstChild);
    }
    let labelDiv = headerDiv.querySelector('.cell-label') as HTMLElement;
    if (!labelDiv) {
      labelDiv = document.createElement('div');
      labelDiv.className = 'cell-label';
      headerDiv.appendChild(labelDiv);
    }
    labelDiv.textContent = locationName;

    // Ensure body exists
    let bodyDiv = contentDiv.querySelector('.cell-body') as HTMLElement;
    if (!bodyDiv) {
      bodyDiv = document.createElement('div');
      bodyDiv.className = 'cell-body';
      // Insert after header
      if (headerDiv.nextSibling) {
        contentDiv.insertBefore(bodyDiv, headerDiv.nextSibling);
      } else {
        contentDiv.appendChild(bodyDiv);
      }
    }
    bodyDiv.innerHTML = anomalyDisplay;

    // Ensure researchers container exists
    let researchersContainer = contentDiv.querySelector('.cell-researchers') as HTMLElement;
    if (!researchersContainer) {
      researchersContainer = document.createElement('div');
      researchersContainer.className = 'cell-researchers';
      contentDiv.appendChild(researchersContainer);
    }

    // Get existing avatars
    const existingAvatars = new Map<string, HTMLElement>();
    researchersContainer.querySelectorAll('.researcher-avatar').forEach(avatar => {
      const id = avatar.getAttribute('data-researcher-id');
      if (id) {
        existingAvatars.set(id, avatar as HTMLElement);
      }
    });

    // Update or create avatars for current researchers
    researchersHere.forEach((r, idx) => {
      const initials = r.data.name.split(' ').map(n => n[0]).join('').substring(0, 2);
      const leftPos = this.locationId === 'control_room' 
        ? `${20 + idx * 50}px` 
        : `${15 + (idx % 2) * 60}px`;
      const bottomPos = '12px';

      let avatar = existingAvatars.get(r.id);
      if (!avatar) {
        // Create new avatar
        avatar = document.createElement('div');
        avatar.className = 'researcher-avatar';
        avatar.setAttribute('data-researcher-id', r.id);
        avatar.setAttribute('tabindex', '0');
        avatar.textContent = initials;
        
        // Add direct click handler as backup
        avatar.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          AudioManager.getInstance().playClick();
          if (this.onSelectResearcher) {
            this.onSelectResearcher(r.id);
          }
        });
        
        researchersContainer.appendChild(avatar);
      } else {
        // Remove from map so we know it's still needed
        existingAvatars.delete(r.id);
      }

      // Update position and content
      avatar.style.left = leftPos;
      avatar.style.bottom = bottomPos;
      if (avatar.textContent !== initials) {
        avatar.textContent = initials;
      }
      
      // Add selected class if this researcher is selected
      const isSelected = selected.type === 'researcher' && selected.id === r.id;
      if (isSelected) {
        avatar.classList.add('selected');
      } else {
        avatar.classList.remove('selected');
      }
    });

    // Remove avatars that are no longer in this room
    existingAvatars.forEach((avatar) => {
      avatar.remove();
    });

    // Handle breached anomaly avatar in TARGET cell (where it escaped TO)
    let breachedAvatar = researchersContainer.querySelector('.breached-anomaly-avatar') as HTMLElement;
    
    if (breachedInHere) {
      // Create or update breached anomaly avatar
      if (!breachedAvatar) {
        breachedAvatar = document.createElement('div');
        breachedAvatar.className = 'breached-anomaly-avatar';
        breachedAvatar.setAttribute('data-breached-id', breachedInHere.originalAnomaly.data.code);
        breachedAvatar.setAttribute('tabindex', '0');
        
        // Use first letter of anomaly name or a breach symbol
        const initial = breachedInHere.originalAnomaly.data.name.charAt(0).toUpperCase();
        const innerSpan = document.createElement('span');
        innerSpan.className = 'breached-avatar-text';
        innerSpan.textContent = initial;
        breachedAvatar.appendChild(innerSpan);
        
        // Add click handler to select breached anomaly
        breachedAvatar.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          // Select the breached anomaly (could trigger a modal or info panel)
          this.onSelect(this.locationId);
        });
        
        researchersContainer.appendChild(breachedAvatar);
      }
      
      // Update if needed
      const initial = breachedInHere.originalAnomaly.data.name.charAt(0).toUpperCase();
      const innerSpan = breachedAvatar.querySelector('.breached-avatar-text');
      if (innerSpan && innerSpan.textContent !== initial) {
        innerSpan.textContent = initial;
      }
    } else if (breachedAvatar) {
      // Remove breached avatar if no longer present
      breachedAvatar.remove();
    }
  }

  private renderDisappearedCell(): void {
    this.container.className = 'cell-display disappeared-cell';
    this.container.innerHTML = `
      <div class="cell-content">
        <div class="cell-header">
          <div class="cell-label">CELL 4</div>
        </div>
        <div class="cell-body">
          <div class="disappeared-message">
            <div class="disappeared-title">Lost in Time and Space</div>
            <div class="disappeared-description">This location no longer exists in our reality.</div>
          </div>
        </div>
      </div>
    `;
  }
}


